import React from "react";
import { Col, Row } from "reactstrap";
import {
  useContests,
  useContestToProblems,
  useFastRanking,
  useFirstRanking,
  useShortRanking,
  useUserSubmission,
} from "../../api/APIClient";
import { caseInsensitiveUserId, isAccepted } from "../../utils";
import { formatMomentDate, getToday } from "../../utils/DateUtil";
import { RankingEntry } from "../../interfaces/RankingEntry";
import { ContestId, ProblemId } from "../../interfaces/Status";
import Problem from "../../interfaces/Problem";
import { calcStreak, countUniqueAcByDate } from "../../utils/StreakCounter";
import { isRatedContest } from "../../utils/ContestClassifier";

const findCountInRanking = (
  ranking: RankingEntry[],
  userId: string
): number => {
  const entry = ranking.find(
    (r) => caseInsensitiveUserId(r.user_id) === userId
  );
  return entry ? entry.count : 0;
};

interface Props {
  userId: string;
}

export const Achievement: React.FC<Props> = (props) => {
  const contests = useContests().data ?? [];
  const contestToProblems =
    useContestToProblems() ?? new Map<ContestId, Problem[]>();
  const userSubmissions = useUserSubmission(props.userId) ?? [];
  const dailyCount = countUniqueAcByDate(userSubmissions);
  const { longestStreak, currentStreak, prevDateLabel } = calcStreak(
    dailyCount
  );
  const shortRanking = useShortRanking() ?? [];
  const fastRanking = useFastRanking() ?? [];
  const firstRanking = useFirstRanking() ?? [];

  const solvedCount = new Set(
    userSubmissions.filter((s) => isAccepted(s.result)).map((s) => s.problem_id)
  ).size;
  const shortCount = findCountInRanking(shortRanking, props.userId);
  const firstCount = findCountInRanking(firstRanking, props.userId);
  const fastCount = findCountInRanking(fastRanking, props.userId);

  const ratedProblemIds = new Set(
    contests
      .flatMap((contest) => {
        const contestProblems = contestToProblems.get(contest.id);
        const isRated = isRatedContest(contest, contestProblems?.length ?? 0);
        return isRated && contestProblems ? contestProblems : [];
      })
      .map((problem) => problem.id)
  );
  const acceptedRatedSubmissions = userSubmissions
    .filter((s) => isAccepted(s.result))
    .filter((s) => ratedProblemIds.has(s.problem_id));
  acceptedRatedSubmissions.sort((a, b) => a.id - b.id);
  const ratedPointMap = new Map<ProblemId, number>();
  acceptedRatedSubmissions.forEach((s) => {
    ratedPointMap.set(s.problem_id, s.point);
  });
  const ratedPointSum = Array.from(ratedPointMap.values()).reduce(
    (sum, point) => sum + point,
    0
  );

  const achievements = [
    { key: "Accepted", value: solvedCount },
    { key: "Shortest Code", value: shortCount },
    { key: "Fastest Code", value: fastCount },
    { key: "First AC", value: firstCount },
    { key: "Rated Point Sum", value: ratedPointSum },
  ];

  const yesterdayLabel = formatMomentDate(getToday().add(-1, "day"));
  const isIncreasing = prevDateLabel >= yesterdayLabel;

  const streakSum = dailyCount.length;

  return (
    <>
      <Row className="my-3">
        {achievements.map(({ key, value }) => (
          <Col key={key} className="text-center" xs="6" md="3">
            <h6>{key}</h6>
            <h3>{value}</h3>
          </Col>
        ))}
        <Col key="Longest Streak" className="text-center" xs="6" md="3">
          <h6>Longest Streak</h6>
          <h3>{longestStreak} days</h3>
        </Col>
        <Col key="Current Streak" className="text-center" xs="6" md="3">
          <h6>Current Streak</h6>
          <h3>{isIncreasing ? currentStreak : 0} days</h3>
          <h6 className="text-muted">{`Last AC: ${prevDateLabel}`}</h6>
        </Col>
        <Col key="Streak Sum" className="text-center" xs="6" md="3">
          <h6>Streak Sum</h6>
          <h3>{streakSum} days</h3>
        </Col>
        <Col />
      </Row>
    </>
  );
};
